import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { makeCurrencyMask } from '@app/functions';
import { AdditionalPayItem } from '@app/models/payroll/additional-pay-item.model';
import { Paystub } from '@app/models/payroll/company/paystub.model';
import { HourlyPayItem } from '@app/models/payroll/hourly-pay-item.model';
import { Payroll } from '@app/models/payroll/payroll.model';
import { ReimbursementItem } from '@app/models/payroll/reimbursement-item.model';
import { SalaryPayItem } from '@app/models/payroll/salary-pay-item.model';
import {
    InsurableDollarPayGrouping,
    InsurableHourlyPayGrouping,
} from '@app/modules/payroll/interfaces/insurable-hours-pay-item-group.interface';
import { AnalyticService, AuthService, NotifyService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { SortDirection } from '@app/types';
import { formatLocalizedNumber, Locale, parseLocalizedNumber } from '@app/types/translatable.type';
import { PayrollSalaryItemNamesDisplayOptions } from '@app/types/translatables/payroll-item-names-display.options';
import { BaseForm } from '@forms/base.form';
import { TranslateService } from '@ngx-translate/core';
import { InsurableHoursService, PaystubLazyLoadService } from '@payroll/services';
import { debounce, flatten } from 'lodash-es';
import { InsurableHoursForm } from '../insurable-hours-form/insurable-hours.form';

const DEBOUNCE_TIME = 1000;

@Component({
    selector: 'app-form-payroll-edit[payroll]',
    styleUrls: ['./payroll-edit.style.scss'],
    templateUrl: './payroll-edit.template.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PayrollEditForm extends BaseForm implements OnInit {
    @ViewChild('deleteConfirmationDialog', { static: true })
    deleteConfirmationDialog!: DeprecatedConfirmationDialogComponent;
    @ViewChild('editInsurableHoursCaptureForm', { static: true }) editInsurableHoursCaptureForm?: InsurableHoursForm;

    @Input() payroll!: Payroll;
    @Input() showForm!: boolean;
    @Output() payrollChange: EventEmitter<Payroll> = new EventEmitter<Payroll>();
    @Output() sortDirectionChange = new EventEmitter<SortDirection>();
    @Output() disableUserFromClickingNextStepEmitter: EventEmitter<boolean> = new EventEmitter<boolean>();

    readonly OVERTIME_MULTIPLIER = '1.5';
    readonly PayrollSalaryItemNamesDisplayOptions = PayrollSalaryItemNamesDisplayOptions;

    displayInsurableHoursOtherIncomeModal = false;
    currentSelectedPaystub: Paystub | null = null;
    employeeName = '';
    insurableHoursAdditionalPayItemsTotal = '';
    displayInsurableHoursErrorString = false;
    insurableHoursErrorString = '';
    allowRecalculation = true;

    currency = makeCurrencyMask({}, this.translateService.currentLang as Locale);

    sortDirection: SortDirection = 'DESC';
    timer: ReturnType<typeof setTimeout> | null = null;
    bonusTaxWarning = false;
    isShowingAdditionalPayItemsTaxWarning = false;
    additionalPayItemsTaxWarningItemName = '';

    onPayItemChange = debounce(
        async (
            payItem: AdditionalPayItem | SalaryPayItem | HourlyPayItem | ReimbursementItem,
            amount: string | number
        ): Promise<void> => {
            if (!(payItem instanceof HourlyPayItem)) {
                payItem.amount = this.stripCurrencySymbolsToNumber(amount);
            } else {
                payItem.hours = this.stripCurrencySymbolsToNumber(amount);
            }

            payItem instanceof ReimbursementItem ? this.saveItem(payItem) : this.savePayItem(payItem);
        },
        DEBOUNCE_TIME
    );

    constructor(
        public router: Router,
        protected auth: AuthService,
        protected notify: NotifyService,
        private features: FeatureService,
        protected analytics: AnalyticService,
        public paystubService: PaystubLazyLoadService,
        private insurableHoursService: InsurableHoursService,
        private translateService: TranslateService
    ) {
        super();
    }

    ngOnInit(): void {
        this.features.has(FeatureFlag.bonusTaxWarning).then((bonusTaxWarning) => {
            this.bonusTaxWarning = bonusTaxWarning;
        });
    }

    /**
     * Get a localized display value for a pay item amount
     * @param item - The pay item to get the formatted amount for, or a direct number value
     * @returns Formatted number string based on current locale
     */
    getFormattedAmount(
        item: AdditionalPayItem | SalaryPayItem | HourlyPayItem | ReimbursementItem | number,
        decimalPlaces: number = 2
    ): string | undefined {
        const amount = typeof item === 'number' ? item : item.amount;
        return formatLocalizedNumber(amount, {
            locale: this.translateService.currentLang as Locale,
            decimalPlaces,
        });
    }

    /**
     * Get a localized display value for hours
     * @param item - The hourly pay item or a direct number value
     * @returns Formatted number string based on current locale
     */
    getFormattedHours(item: HourlyPayItem | number, decimalPlaces: number = 2): string | undefined {
        const hours = typeof item === 'number' ? item : Number(item.hours) || 0;
        return formatLocalizedNumber(hours, {
            locale: this.translateService.currentLang as Locale,
            decimalPlaces,
        });
    }

    /**
     * Set a localized value for a pay item amount
     * @param item - The pay item to set the amount for
     * @param value - The localized string value to parse
     */
    setFormattedAmount(
        item: AdditionalPayItem | SalaryPayItem | HourlyPayItem | ReimbursementItem,
        value: string
    ): void {
        const parsedValue = parseLocalizedNumber(value, this.translateService.currentLang as Locale);
        this.disableUserFromClickingNextStepEmitter.emit(true);
        this.onPayItemChange(item, parsedValue ?? 0);
    }

    /**
     * Set a localized value for hours on a hourly pay item
     * @param item - The hourly pay item to set the hours for
     * @param value - The localized string value to parse
     */
    setFormattedHours(item: HourlyPayItem, value: string): void {
        const parsedValue = parseLocalizedNumber(value, this.translateService.currentLang as Locale);
        this.disableUserFromClickingNextStepEmitter.emit(true);
        this.onPayItemChange(item, parsedValue ?? 0);
    }

    async reloadCompanyAdditionalIncomes(): Promise<void> {
        this.editInsurableHoursCaptureForm?.reloadCompanyAdditionalIncomes();
    }

    deletePaystub(paystub: Paystub): void {
        this.deleteConfirmationDialog.show().then(async (confirmDelete) => {
            if (!confirmDelete) {
                return;
            }

            try {
                await this.paystubService.delete(paystub);
            } catch (e) {
                this.notify.error(e);
            }
        });
    }

    async savePaystub(paystub: Paystub): Promise<void> {
        try {
            await this.paystubService.save(paystub);
            await this.paystubService.refreshPaystub(paystub.id);
        } catch (e) {
            this.notify.error(e);
        }
    }

    shouldOtherIncomeInsurableHoursBeDisplayed(displayInsurableHoursModal: boolean): void {
        this.insurableHoursAdditionalPayItemsTotal = '0.00';
        this.displayInsurableHoursOtherIncomeModal = displayInsurableHoursModal;
        this.employeeName = this.currentSelectedPaystub != null ? this.currentSelectedPaystub.employeeName : '';
    }

    /**
     * Whether the additional pay item amount is larger than the Regular Pay amount.
     * This may cause tax issues.
     */
    shouldWarnForAdditionalPayItem(item: AdditionalPayItem): boolean {
        if (!this.bonusTaxWarning) {
            return false;
        }

        const paystub = this.paystubService.findById(item.paystubId);

        if (!paystub) {
            return false;
        }

        const regularPayItem =
            paystub.salaryPayItems.find((item) => item.name === 'Regular Pay') ??
            paystub.hourlyPayItems.find((item) => item.name === 'Regular Pay');

        if (!regularPayItem) {
            return false;
        }

        return item.amount > regularPayItem.amount;
    }

    /**
     * Show the additional pay items tax warning if necessary
     */
    checkForOvertax(item: AdditionalPayItem): void {
        if (this.shouldWarnForAdditionalPayItem(item)) {
            this.isShowingAdditionalPayItemsTaxWarning = true;
            this.additionalPayItemsTaxWarningItemName = item.displayName;
        }
    }

    savePayItem(item: AdditionalPayItem | SalaryPayItem | HourlyPayItem): void {
        if (item.isClean()) {
            return;
        }

        item.userModified = true;

        if (item instanceof AdditionalPayItem) {
            this.checkForOvertax(item);
        }

        this.saveItem(item);
    }

    saveItem(item: AdditionalPayItem | SalaryPayItem | HourlyPayItem | ReimbursementItem): void {
        item.param('company', this.auth.company.id)
            .param('payroll', this.payroll.id)
            .save()
            .then((_model) => {
                this.trackItemSave(item);
                this.paystubService.refreshPaystub(item.paystubId);
                this.disableUserFromClickingNextStepEmitter.emit(false);
            })
            .catch((_) => {
                this.notify.error('forms.payroll-edit.couldNotSavePayrollChanges');
                this.disableUserFromClickingNextStepEmitter.emit(false);
            });
    }

    addOvertime(paystub: Paystub, primaryCompensationHourlyItem: HourlyPayItem): HourlyPayItem {
        const overtimePayItem = new HourlyPayItem({
            paystubId: paystub.id,
            compensationId: primaryCompensationHourlyItem.compensationId,
            multiplier: this.OVERTIME_MULTIPLIER,
            hours: 0,
            name: 'overtime',
            userModified: true,
            rate: Number(primaryCompensationHourlyItem.rate),
        });

        paystub.hourlyPayItems = this.sortHourlyPayItems([...paystub.hourlyPayItems, overtimePayItem]);
        return overtimePayItem;
    }

    sortHourlyPayItems(hourlyPayItems: HourlyPayItem[]): HourlyPayItem[] {
        const primaryHourlyPayItems = hourlyPayItems
            .filter(
                (hourlyPayItem: HourlyPayItem) =>
                    hourlyPayItem.name === 'Regular Pay' || hourlyPayItem.name === 'overtime'
            )
            .sort((a: HourlyPayItem, b: HourlyPayItem) => {
                return a.compensationId > b.compensationId ? -1 : 1;
            });
        const additionalPayItems = hourlyPayItems.filter(
            (hourlyPayItem: HourlyPayItem) => hourlyPayItem.name !== 'Regular Pay' && hourlyPayItem.name !== 'overtime'
        );
        const finalSortedHourlyPayItems = [...primaryHourlyPayItems, ...additionalPayItems];
        return finalSortedHourlyPayItems;
    }

    addAdditionalPayItem(paystub: Paystub, name: string): void {
        const newPayItem = new AdditionalPayItem({ paystubId: paystub.id, name, amount: 0 });
        paystub.additionalPayItems = [...paystub.additionalPayItems, newPayItem];
    }

    addReimbursement(paystub: Paystub): void {
        paystub.reimbursementItem = new ReimbursementItem({
            paystubId: paystub.id,
            amount: 0,
        });
    }

    hasOvertime(paystub: Paystub, primaryHourlyPayItem?: HourlyPayItem): boolean {
        if (!primaryHourlyPayItem) {
            return Boolean(
                paystub.hourlyPayItems.some((hourlyPayItem) => hourlyPayItem.multiplier === this.OVERTIME_MULTIPLIER)
            );
        }
        const compensationId = primaryHourlyPayItem.compensationId;
        const overtimeMatchedCompensations = paystub.hourlyPayItems.filter(
            (hourlyPayItem: HourlyPayItem) => hourlyPayItem.compensationId === compensationId
        );
        return Boolean(overtimeMatchedCompensations.length > 1);
    }

    hasBonus(paystub: Paystub): boolean {
        return paystub.additionalPayItems.some((additionalPayItem) => additionalPayItem.name === 'bonus');
    }

    hasCommission(paystub: Paystub): boolean {
        return paystub.additionalPayItems.some((additionalPayItem) => additionalPayItem.name === 'commission');
    }

    hasOther(paystub: Paystub): boolean {
        return paystub.additionalPayItems.some((additionalPayItem) => additionalPayItem.name === 'other');
    }

    stripCurrencySymbolsToNumber(val: string | number): number {
        if (val) {
            this.resetCursorPosition();
        }
        return Number(val.toString().replace(/[^0-9.-]+/g, ''));
    }

    submitAndValidate(form: BaseForm): boolean {
        if (form) {
            form.submit();
            return form.valid;
        }
        return true;
    }

    async determineIfThereAreEnoughHoursToSubtractFromSalary(
        additionalPayItems: AdditionalPayItem[]
    ): Promise<boolean> {
        if (!additionalPayItems.length || !this.currentSelectedPaystub) {
            return true;
        }

        const areThereEnoughHoursToSubtractFromSalaryMessage =
            await this.insurableHoursService.determineIfThereAreEnoughHoursToSubtractFromSalary(
                additionalPayItems,
                this.payroll.id,
                this.currentSelectedPaystub.id
            );
        if (areThereEnoughHoursToSubtractFromSalaryMessage.toLowerCase() === 'success') {
            return true;
        }
        this.allowRecalculation = false;
        this.displayInsurableHoursErrorString = true;
        this.insurableHoursErrorString = areThereEnoughHoursToSubtractFromSalaryMessage;
        return false;
    }

    async onSaveInsurableHoursCaptureItems(
        insurableHourlyPayItems: InsurableHourlyPayGrouping[],
        insurableDollarPayItems: InsurableDollarPayGrouping[],
        insurableHoursCaptureForm: BaseForm
    ): Promise<void> {
        if (!this.submitAndValidate(insurableHoursCaptureForm) || !this.currentSelectedPaystub) {
            return;
        }
        this.allowRecalculation = true;
        this.displayInsurableHoursErrorString = false;
        this.insurableHoursErrorString = '';

        const allHourlyPayItems = flatten(
            insurableHourlyPayItems.map((insurablePayItemGroup: InsurableHourlyPayGrouping) =>
                insurablePayItemGroup.payItems.map((payItem: AdditionalPayItem) => payItem)
            )
        );
        const allDollarPayItems = insurableDollarPayItems.map(
            (insurableDollarPayItem: InsurableDollarPayGrouping) => insurableDollarPayItem.payItem
        );
        const allPayItems = [...allHourlyPayItems, ...allDollarPayItems];

        const modifiedPayItems = allPayItems.filter((payItem: AdditionalPayItem) => {
            return this.determineIfAdditionalPayItemAmountHasBeenModified(
                payItem,
                this.currentSelectedPaystub?.additionalPayItems || []
            );
        });

        if (!this.currentSelectedPaystub?.hourlyEmployee) {
            const isThereEnoughHoursToSubtractFromSalary =
                await this.determineIfThereAreEnoughHoursToSubtractFromSalary(modifiedPayItems);
            if (!isThereEnoughHoursToSubtractFromSalary) {
                this.editInsurableHoursCaptureForm?.form.resetForm(this.editInsurableHoursCaptureForm.form.value);
                return;
            }
        }

        try {
            const additionalPayItemsBulkUpsertRequestAndMessage =
                await this.insurableHoursService.saveBulkAdditionalPayItems(
                    modifiedPayItems,
                    this.payroll.id,
                    this.currentSelectedPaystub.id
                );
            if (additionalPayItemsBulkUpsertRequestAndMessage.toLowerCase() !== 'success') {
                this.allowRecalculation = false;
                this.displayInsurableHoursErrorString = true;
                this.insurableHoursErrorString = additionalPayItemsBulkUpsertRequestAndMessage;
                this.editInsurableHoursCaptureForm?.form.resetForm(this.editInsurableHoursCaptureForm.form.value);
                return;
            }
        } catch (error) {
            this.notify.error(ErrorParser.parse(error as Record<string, unknown>));
        }

        modifiedPayItems.forEach((payItem: AdditionalPayItem) => {
            this.trackOtherIncomeAnalytics(payItem.name);
        });
        await this.paystubService.refreshPaystub(this.currentSelectedPaystub.id);
        this.shouldOtherIncomeInsurableHoursBeDisplayed(false);
        this.currentSelectedPaystub = null;
    }

    determineIfAdditionalPayItemAmountHasBeenModified(
        payItem: AdditionalPayItem,
        additionalPayItemsOnPaystub: AdditionalPayItem[]
    ): boolean {
        if (payItem.id === null) {
            if (payItem.amount === 0 && payItem.hours === 0) {
                return false;
            } else {
                return true;
            }
        } else {
            const additionalPayItemBeingModified = additionalPayItemsOnPaystub.find(
                (additionalPayItem: AdditionalPayItem) => additionalPayItem.id === payItem.id
            );
            if (additionalPayItemBeingModified === undefined) {
                return false;
            }
            if (
                additionalPayItemBeingModified.amount !== payItem.amount ||
                additionalPayItemBeingModified.hours !== payItem.hours
            ) {
                return true;
            } else {
                return false;
            }
        }
    }

    onSaveBonusPayItems(paystub?: Paystub): void {
        if (!paystub) return;
        Promise.all(this.saveAdditionalPayItems(paystub.bonusPayItems)).then(() =>
            this.paystubService.refreshPaystub(paystub.id)
        );
    }

    openHelpArticle(): void {
        const url = 'https://humi.zendesk.com/hc/en-us/articles/360041853833-Bonus-Payments';
        window.open(url, '_blank');
    }

    onSort(): void {
        if (this.sortDirection === 'DESC') {
            this.sortDirection = 'ASC';
        } else {
            this.sortDirection = 'DESC';
        }
        this.sortDirectionChange.emit(this.sortDirection);
    }

    trackOtherIncomeAnalytics(otherIncomeName: string): void {
        switch (otherIncomeName) {
            case 'bonus_work_related':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeBonusWorkRelatedSave);
                break;
            case 'bonus_discretionary':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeBonusDiscretionarySave);
                break;
            case 'comission':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeComissionSave);
                break;
            case 'car':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeCarAllowanceSave);
                break;
            case 'extra_pay':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeExtraPaySave);
                break;
            case 'double_overtime':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeDoubleOvertimeSave);
                break;
            case 'vacation_payout':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeVacationPayoutSave);
                break;
            case 'cell_phone':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeCellPhoneAllowanceSave);
                break;
            case 'holiday_pay':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeHolidayPaySave);
                break;
            case 'time_worked_on_holiday':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeTimeWorkedOnHolidaySave);
                break;
            case 'sick_pay':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeSickPaySave);
                break;
            case 'retro_pay':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeRetroactivePaySave);
                break;
            case 'retro_pay_hourly':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeRetroactivePayHourlySave);
                break;
            case 'controlled_tips':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeControlledTipsSave);
                break;
            case 'other_pay':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeOtherPaySave);
                break;
            case 'severance':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeSeveranceSave);
                break;
            case 'pay_in_lieu':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomePayInLieuOfNoticeSave);
                break;
            case 'vacation_payout_for_time_off':
                this.analytics.trackEvent(AnalyticEvents.PayrollEditStepOtherIncomeVacationPayoutTimeOffSave);
                break;
            default:
                break;
        }
    }
    private resetCursorPosition(): void {
        const focusedInput = <HTMLInputElement>document.activeElement;
        const selectionStart = focusedInput.selectionStart;
        setTimeout(() => {
            focusedInput.selectionStart = selectionStart;
            focusedInput.selectionEnd = selectionStart;
        });
    }

    private saveAdditionalPayItems(payItems: AdditionalPayItem[]): Promise<AdditionalPayItem | void>[] {
        return payItems.reduce((accumulator: Promise<AdditionalPayItem | void>[], payItem) => {
            payItem.param('company', this.auth.company.id);

            if (!payItem.isDirty()) {
                return accumulator;
            }

            if (!payItem.isZero) {
                return [...accumulator, payItem.save().then((item) => this.trackItemSave(item))];
            }

            if (payItem.isPersisted && payItem.isZero) {
                return [...accumulator, payItem.delete()];
            }

            return accumulator;
        }, []);
    }

    private trackItemSave(item: AdditionalPayItem | SalaryPayItem | HourlyPayItem | ReimbursementItem): void {
        if (item instanceof ReimbursementItem) {
            return this.analytics.trackEvent(
                this.payroll.recurring
                    ? AnalyticEvents.PayrollRecurringPayrollEditStepAddReimbursementAutosave
                    : AnalyticEvents.PayrollOffCycleEditStepAddReimbursementAutosave
            );
        }

        // Not tracking anything else for recurring
        if (this.payroll.recurring) {
            return;
        }

        if (item instanceof SalaryPayItem || item instanceof HourlyPayItem) {
            return;
        }

        if (item.isBonusPayItem) {
            return this.analytics.trackEvent(AnalyticEvents.PayrollOffCycleEditStepAddBonusSave);
        }

        if (item.name == 'commission') {
            return this.analytics.trackEvent(AnalyticEvents.PayrollOffCycleEditStepAddCommissionAutosave);
        }

        if (item.isNonRequiredItem) {
            return this.analytics.trackEvent(AnalyticEvents.PayrollOffCycleEditStepAddOtherIncomeSave);
        }
    }
}
